import type { ReactNode } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
    show: boolean;
    title: string;
    /** Label for the (danger) confirm button. */
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    /** Modal body — plain text or arbitrary content (e.g. a type-to-confirm field). */
    children?: ReactNode;
    /** Extra disable condition for the confirm button (in-flight, type-to-confirm, blocked). */
    confirmDisabled?: boolean;
    /** Override the cancel label; defaults to the shared "common.cancel" string. */
    cancelLabel?: string;
}

// Shared confirm modal: a titled header with a close button, a body, and a
// cancel/confirm footer. Replaces the hand-rolled delete modals across the
// mapping tabs, the users table, the application admin (type-to-confirm), and
// the profile page.
export function ConfirmDialog({
    show,
    title,
    confirmLabel,
    onConfirm,
    onCancel,
    children,
    confirmDisabled = false,
    cancelLabel,
}: ConfirmDialogProps) {
    const { t } = useTranslation();
    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h5">{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{children}</Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancel}>
                    {cancelLabel ?? t('common.cancel')}
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={confirmDisabled}>
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
